import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Comment } from "../../../utils/types";
import { useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { editClientNote, deleteClientNote } from "../../../utils/helper";

import { useAuth } from "../../../context/AuthContext";

interface CommentBoxProps {
  comments: Comment[];
}

export const CommentBox = (props: CommentBoxProps) => {
  const { jwt } = useAuth()
  const queryClient = useQueryClient();

  const { comments } = props;

  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    commentId: string | null;
  }>({ anchorEl: null, commentId: null });

  const [editingComment, setEditingComment] = useState<{
    id: string | null;
    note: string;
    clientId?: string;
  }>({ id: null, note: "" });

  const editClientNoteMutation = useMutation({
    mutationFn: ([clientId, note]: [string, Comment]) => editClientNote(clientId, note, jwt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const deleteClientNoteMutation = useMutation({
    mutationFn: (commentId: string) => deleteClientNote(commentId, jwt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>, commentId: string) => {
    setMenuState({ anchorEl: event.currentTarget, commentId });
  };

  const handleEdit = (comment: Comment, clientId: string) => {
    setEditingComment({ id: comment.id as string, note: comment.note, clientId });
    handleClose();
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingComment((prev) => ({ ...prev, note: e.target.value }));
  };

  const handleEditSave = () => {
    if (editingComment.id && editingComment.clientId) {
      editClientNoteMutation.mutateAsync([
        editingComment.clientId,
        { id: editingComment.id, note: editingComment.note },
      ]);
      setEditingComment({ id: null, note: "" });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSave();
    }
  };

  const handleClose = () => {
    setMenuState({ anchorEl: null, commentId: null });
  };

  return (
    <>
      {[...comments].reverse().map((c) => (
        <Box key={c.id} sx={{ mb: 1 }}>
          <Card
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 48,
            }}
            onClick={(event) => handleClick(event, c.id as string)}
          >
            {editingComment.id === c.id ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  value={editingComment.note}
                  onChange={handleEditChange}
                  onKeyPress={handleKeyPress}
                  size="small"
                  multiline
                  autoFocus
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleEditSave}
                >
                  Save
                </Button>
              </Box>
            ) : (
              <>
                <Typography variant="body2">{c.note}</Typography>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[500]
                          : theme.palette.grey[700],
                      mt: 1,
                    }}
                  >
                    {c.createdAt}
                  </Typography>
                </Box>
              </>
            )}
          </Card>
          <Menu
            id={`comment-menu-${c.id}`}
            anchorEl={menuState.anchorEl}
            open={menuState.commentId === c.id}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem
              onClick={() => handleEdit(c, c.id as string)}
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                deleteClientNoteMutation.mutate(c.id as string);
                handleClose();
              }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Box>
      ))}
    </>
  );
};
